import { useState, useEffect, memo, type CSSProperties } from "react";
import { type ImageContentFit } from "expo-image";
import { SignedSnapshot, AllowedWidth } from "..";
import { useSignedUrl } from "../hooks/use-signed-url";

export interface SnapshotImageProps {
  /** Signed snapshot data, or null/undefined if no snapshot available */
  snapshot: SignedSnapshot | null | undefined;
  width: AllowedWidth | null | undefined;
  /** Style for the image */
  style?: CSSProperties;
  /** How the image should fit within its container */
  contentFit?: ImageContentFit;
  /** Color for the placeholder when no snapshot is available */
  placeholderColor?: string;
}

export const SnapshotImage = memo(function SnapshotImage({
  snapshot,
  width,
  style,
  contentFit = "cover",
  placeholderColor = "#e0e0e0",
}: SnapshotImageProps) {
  const [hasError, setHasError] = useState(false);
  const imageSrc = useSignedUrl(snapshot, width);

  useEffect(() => {
    setHasError(false);
  }, [snapshot?.url]);

  const shouldShowImage = snapshot && imageSrc && !hasError;

  if (!shouldShowImage) {
    return <div style={{ width: "100%", height: "100%", backgroundColor: placeholderColor, ...style }} />;
  }

  return (
    <div style={{ width: "100%", height: "100%", ...style }}>
      <img
        src={imageSrc}
        style={{
          width: "100%",
          height: "100%",
          objectFit: contentFit === "none" ? "none" : contentFit,
        }}
        onError={() => setHasError(true)}
      />
    </div>
  );
});
